import { useMemo, useState } from "react";
import { trpc } from "../../lib/trpc";
import { useDebounced } from "../../lib/useDebounced";
import { Icon } from "../ui/Icon";
import { SearchSelect } from "../ui/SearchSelect";
import { useIsMobile } from "../../lib/useIsMobile";
// Durum süzgeci Siparişler ekranıyla AYNI kalıp: çoklu SearchSelect + "Açık siparişler" kısayolu,
// AYNI etiketler (tek kaynak). Eskiden burada ayrı bir sözlük vardı ("Sevkiyata Hazır" ↔ "Hazır").
import {
  type SiparisDurumu, DURUM_SECENEKLERI, durumEtiketi, durumRozetSinifi, durumlariSirala, durumSecimOzeti,
  durumSuzgecGirdisi, ACIK_SIPARISLER_AD, ACIK_SIPARISLER_KUME, acikSiparislerSecili, acikSiparislerKisayolu,
  GENEL_MRP_KUME,
} from "../../lib/siparisDurum";
import "./OrderMrpSelector.css";

/**
 * SEÇİLİ SİPARİŞLER İÇİN MRP — sipariş listesi + tekli/çoklu seçim + A4 rapor.
 *
 * Kullanıcı isteği (29 Ağu 2026): "MRP modülünde siparişleri listeleyelim, kullanıcı tekli veya
 * çoklu seçim yaparak seçili siparişlere özel MRP çalıştırsın ve MRP raporunu yazdırabilsin."
 *
 * ⚠ HESAP RAPORDA KOŞAR. Seçim yapıldıktan sonra `/print/mrp-raporu` yeni sekmede açılır ve
 * hesabı O sayfa yapar. Aynı sonucu hem burada hem raporda göstermek iki ayrı gösterim demekti;
 * biri güncellenip diğeri unutulurdu (ayna kuralı). Bu, Teslimat Planı ve Üretim Raporu ile de
 * AYNI kalıp: seçim ekranda, belge kendi sayfasında.
 *
 * ⚠ YALNIZ `view:recipe` İZNİ OLANA gösterilir: rapor ham madde (reçete) kırılımı içerir ve
 * uç da aynı izni arar. Yetkisi olmayana ulaşamayacağı bir düğme göstermek yanıltıcı olurdu.
 * (Eskiden `isSuperAdmin` bayrağıydı; tesise bağlı Sistem Yöneticisini dışarıda bırakıyordu.)
 */

/** Uç sınırı (`order.materialNeedsMulti` max 30) — burada da uygulanır ki kullanıcı hata almasın. */
const MAKS_SECIM = 30;

/**
 * Satırda durum GÖSTERİLİR (etiket/rozet `lib/siparisDurum`): iptal edilmiş sipariş talebe girmez,
 * kullanıcı bunu seçmeden önce görmeli.
 */
/** Talebe GİRMEYEN durumlar — uç ile aynı küme (order.ts MRP_KAPSAM_DISI_DURUMLAR). */
const KAPSAM_DISI = ["cancelled"];

const fmtQty = (n: number) => new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);
const fmtDate = (d: string | null) =>
  d ? new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d)) : "—";

interface SiparisSatiri {
  id: string; code: string; customerName: string; status: string;
  teslimTarihi: string | null; teslimTahmini: boolean; gecikti: boolean;
  items: Array<{ code: string; name: string; qty: number; uom: string }>;
}

export function OrderMrpSelector() {
  const isMobile = useIsMobile();
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  /**
   * Rapor ham madde (reçete) kırılımı içerir → kapı `view:recipe` iznidir, `isSuperAdmin` DEĞİL.
   * Uç (`order.materialNeedsMulti`) 31 Ağu 2026'da izne taşındı; burası bayrakta kalsaydı tesise
   * bağlı Sistem Yöneticisi için uç açık ama KART GÖRÜNMEZ olurdu — yetkisi olduğu hâlde
   * ulaşamayacağı bir özellik. Yetkisi olmayana düğme göstermek de yanıltıcı olurdu.
   */
  const u = meQ.data?.user as { isSuperAdmin?: boolean; permissions?: string[] } | undefined;
  const receteGorunur = !!u && (!!u.isSuperAdmin || (u.permissions ?? []).includes("view:recipe"));

  const [q, setQ] = useState("");
  const qGecikmeli = useDebounced(q, 300);
  /**
   * ÇOK SEÇİMLİ durum (Maya Food, 14 Eki 2026). Varsayılan Tümü. YALNIZ bu listedeki siparişleri
   * daraltır — MRP hesabı (materialNeedsMulti / genel "MRP Çalıştır") DEĞİŞMEZ; ürün türü süzgeci
   * burada bilinçli olarak YOK (seçilen siparişin tüm satırları hesaplanır).
   */
  const [durumlar, setDurumlar] = useState<SiparisDurumu[]>([]);
  const acikSecili = acikSiparislerSecili(durumlar);
  const [secili, setSecili] = useState<string[]>([]);
  const [uyari, setUyari] = useState<string | null>(null);

  const listQ = trpc.order.list.useQuery(
    { page: 1, pageSize: 50, q: qGecikmeli.trim() || undefined, ...durumSuzgecGirdisi(durumlar) },
    { enabled: receteGorunur, staleTime: 30_000 },
  );
  const rows = (listQ.data?.rows ?? []) as unknown as SiparisSatiri[];
  const total = listQ.data?.total ?? 0;
  const seciliKume = useMemo(() => new Set(secili), [secili]);

  if (!receteGorunur) return null;

  function degistir(id: string) {
    setUyari(null);
    setSecili((s) => {
      if (s.includes(id)) return s.filter((x) => x !== id);
      if (s.length >= MAKS_SECIM) {
        setUyari(`En fazla ${MAKS_SECIM} sipariş seçilebilir — her sipariş ayrı bir MRP koşusu demek.`);
        return s;
      }
      return [...s, id];
    });
  }

  /**
   * Listedekileri seçime EKLER — mevcut seçimi SİLMEZ. Eskiden `setSecili(ids)` ile değiştiriyordu:
   * kullanıcı başka bir aramada işaretlediği siparişleri sessizce kaybediyordu.
   * Sınıra takılan olursa kaç tanesinin eklenemediği SÖYLENİR.
   */
  function hepsiniSec() {
    setUyari(null);
    setSecili((mevcut) => {
      const yeni = [...mevcut];
      let eklenemeyen = 0;
      for (const r of rows) {
        if (yeni.includes(r.id)) continue;
        if (yeni.length >= MAKS_SECIM) { eklenemeyen++; continue; }
        yeni.push(r.id);
      }
      if (eklenemeyen > 0) {
        setUyari(`${eklenemeyen} sipariş eklenemedi — en fazla ${MAKS_SECIM} sipariş seçilebilir (her sipariş ayrı bir MRP koşusu).`);
      }
      return yeni;
    });
  }

  function raporAc() {
    if (secili.length === 0) return;
    /**
     * ⚠ `window.open(url, "_blank", "noopener")` SPEC GEREĞİ HER ZAMAN null döner → "engellendi"
     * uyarısı rapor açılsa bile basılırdı. noopener'ı özellik dizesiyle değil, açılan pencerenin
     * `opener` bağını koparıp sağlıyoruz (kod tabanında Teslimat Planı ile aynı çözüm).
     */
    const w = window.open(`/print/mrp-raporu?orderIds=${secili.join(",")}`, "_blank");
    if (w) w.opener = null;
    setUyari(w ? null : "Rapor açılamadı — tarayıcı açılır pencereyi engelledi. Bu site için izin verin.");
  }

  return (
    <div className="d-card oms-card">
      <div className="d-card-head">
        <div className="left">
          <span className="t"><Icon name="list" size={12} /> Seçili Siparişler için MRP</span>
          <span className="s">seçili siparişlere özel · A4 rapor</span>
        </div>
        <div className="oms-actions">
          {/* Sayaç yalnız seçim VARKEN: "0 seçili" bilgi taşımıyor, gürültü ekliyordu. */}
          {secili.length > 0 ? (
            <>
              <span className="oms-sayac"><b>{secili.length}</b> sipariş seçili</span>
              <button type="button" className="btn-md secondary sm" onClick={() => { setSecili([]); setUyari(null); }}>
                <Icon name="x" size={13} />Temizle
              </button>
            </>
          ) : null}
          {/* Seçim yokken düğme SESSİZ (secondary): devre dışı bir BİRİNCİL CTA ekranın en
              dikkat çeken öğesi oluyor ama tıklanamıyor — sahte eylem daveti. */}
          <button
            type="button"
            className={`btn-md sm ${secili.length > 0 ? "primary" : "secondary"}`}
            onClick={raporAc}
            disabled={secili.length === 0}
            title={secili.length === 0 ? "Önce en az bir sipariş seçin" : `${secili.length} sipariş için A4 MRP raporu`}
          >
            <Icon name="printer" size={13} />
            {secili.length > 0 ? `MRP Raporu (${secili.length})` : "MRP Raporu"}
          </button>
        </div>
      </div>

      {/* İki MRP yolu aynı ekranda: üstteki düğme genel talep kümesini kapsar, bu kart SEÇİLENLERİ.
          Fark yazılmazsa kullanıcı hangisinin ne yaptığını ayırt edemiyor.
          ⚠ Genel kümede "açık siparişler" DENMEZ: aynı karttaki "Açık siparişler" kısayolu BAŞKA bir
          küme (Taslak · Onaylı · Üretimde; genel MRP: Onaylı · Üretimde · Hazır). Küme durum adlarıyla
          yazılır ve sunucudaki `MRP_TALEP_DURUMLARI`nın aynasından (`GENEL_MRP_KUME`) türetilir. */}
      <div className="oms-not">
        Üstteki <b>MRP Çalıştır</b> <b>{GENEL_MRP_KUME}</b> durumundaki tüm siparişleri kapsar; burası
        yalnız <b>seçtiklerinizi</b> hesaplar (taslak da girer, iptal girmez).
      </div>

      <div className="oms-filtre">
        <div className="oms-ara">
          <Icon name="search" size={13} />
          <input
            type="search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Sipariş kodu, müşteri ara…" aria-label="Sipariş ara"
          />
        </div>
        {/* Kutu `.oms-durum` (OrderMrpSelector.css) YALNIZ GENİŞLİK verir; yükseklik SearchSelect
            tetikleyicisinden gelir (`--field-h`; mobilde 44 px). Eskiden kutuya native select'ten kalma SABİT
            32 px yükseklik veriliyordu: mobilde 44 px'lik tetikleyiciyi 12 px taşırıp alttaki "Açık
            siparişler" düğmesinin üstüne bindiriyordu (ölçüldü) ve bu yüzden mobil dalı `.dt2-filter`e
            ayrılmıştı. Yükseklik kalkınca iki cihaz TEK sınıfı kullanır: masaüstünde 170 px, mobilde satırı
            doldurur. ⚠ 170 px'te özet KESİLEBİLİR (ölçüldü, 14 Eki: seçim varken temizle düğmesiyle metne
            102 px kalıyor; 42 farklı özetin 24'ü, iki adlı 15 özetin 8'i kesik — 156 px'te 30/42) → tam
            özet `title` ile verilir, kesilen metin sessizce kaybolmaz. */}
        <div className="oms-durum" title={durumSecimOzeti(durumlar, { tam: true })}>
          <SearchSelect
            value={null}
            multiValues={durumlar}
            onChangeMulti={(v) => setDurumlar(durumlariSirala(v))}
            multiSummary={() => durumSecimOzeti(durumlar)}
            options={DURUM_SECENEKLERI}
            onChange={() => {}}
            placeholder="Tüm durumlar"
            searchPlaceholder="Durum ara…"
            showFooter={false}
            emptyText="—"
          />
        </div>
        {/* `.ord-kisayol` = Siparişler ekranındaki kısayolun basılı-hâl görünümü (Orders.css; CSS paketi
            tek parça olduğu için burada da geçerli) → iki ekranda AYNI düğme. */}
        <button
          type="button"
          className="btn-md secondary sm ord-kisayol"
          aria-pressed={acikSecili}
          onClick={() => setDurumlar(acikSiparislerKisayolu(durumlar))}
          title={acikSecili
            ? "Kısayolu kaldır — tüm durumlar"
            : `Yalnız ${ACIK_SIPARISLER_KUME} durumundaki siparişleri listeler — genel MRP kapsamı (${GENEL_MRP_KUME}) DEĞİLDİR`}
        >
          <Icon name={acikSecili ? "check" : "filter"} size={13} />
          {ACIK_SIPARISLER_AD}
          <span className="ord-kisayol-kume">({ACIK_SIPARISLER_KUME})</span>
        </button>
        <button type="button" className="btn-md secondary sm" onClick={hepsiniSec} disabled={rows.length === 0}>
          Listedekileri seç
        </button>
        <span className="grow" />
        <span className="oms-toplam">{total} sipariş{total > rows.length ? ` · ilk ${rows.length} gösteriliyor` : ""}</span>
      </div>

      {uyari ? <div className="oms-uyari" role="alert"><Icon name="info" size={13} />{uyari}</div> : null}

      <div className="oms-scroll">
        {listQ.isLoading ? (
          <div className="oms-bos">Siparişler yükleniyor…</div>
        ) : listQ.isError ? (
          <div className="oms-bos err" role="alert">Siparişler yüklenemedi: {listQ.error.message}</div>
        ) : rows.length === 0 ? (
          <div className="oms-bos">Süzgece uyan sipariş yok.</div>
        ) : (
          isMobile ? (
            /* ≤767px: proje deseni TABLO → KART. 5 sütunlu tablo telefonda okunmuyordu. */
            <div className="oms-kartlar">
              {rows.map((o) => {
                const sec = seciliKume.has(o.id);
                const disi = KAPSAM_DISI.includes(o.status);
                return (
                  <label key={o.id} className={`oms-kart${sec ? " sec" : ""}`}>
                    <input type="checkbox" checked={sec} onChange={() => degistir(o.id)} aria-label={`${o.code} siparişini seç`} />
                    <span className="govde">
                      <span className="ust">
                        <b className="mono">{o.code}</b>
                        <span className={`badge-pill ${durumRozetSinifi(o.status)}`}>{durumEtiketi(o.status)}</span>
                      </span>
                      <span className="mus">{o.customerName}</span>
                      {disi ? <span className="oms-disi">İptal — MRP talebine girmez</span> : null}
                      <span className="kal">
                        {o.items.map((it, i) => (
                          <span key={i}>{it.name} <b className="mono">{fmtQty(it.qty)} {it.uom}</b></span>
                        ))}
                      </span>
                      <span className="tar mono">
                        Teslim: <span className={o.gecikti ? "gec" : undefined}>{fmtDate(o.teslimTarihi)}</span>
                        {o.teslimTahmini ? <span className="oms-yildiz">*</span> : null}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          ) : (
          <table className="oms-tbl">
            {/* Genişlikler CSS'te (`table-layout: fixed`); satır içi `style` ile spacing verilmez. */}
            <colgroup>
              <col className="c-sec" />
              <col className="c-kod" />
              <col className="c-durum" />
              <col className="c-mus" />
              <col />
              <col className="c-teslim" />
            </colgroup>
            <thead>
              <tr>
                <th aria-label="Seçim" />
                <th>Sipariş</th>
                <th>Durum</th>
                <th>Müşteri</th>
                <th>Kalemler</th>
                <th>Teslim</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => {
                const sec = seciliKume.has(o.id);
                const disi = KAPSAM_DISI.includes(o.status);
                return (
                  /* Satırın TAMAMI seçim hedefi (dokunma alanı 15px onay kutusuyla sınırlı kalmasın).
                     Onay kutusu yine gerçek `<input>`; satır tıklaması onu değiştirir. */
                  <tr
                    key={o.id}
                    className={sec ? "sec" : undefined}
                    onClick={() => degistir(o.id)}
                  >
                    <td>
                      <input
                        type="checkbox" checked={sec}
                        onChange={() => degistir(o.id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`${o.code} siparişini seç`}
                      />
                    </td>
                    <td className="mono oms-kod">{o.code}</td>
                    <td>
                      <span className={`badge-pill ${durumRozetSinifi(o.status)}`}>{durumEtiketi(o.status)}</span>
                      {disi ? <span className="oms-disi">talebe girmez</span> : null}
                    </td>
                    <td>{o.customerName}</td>
                    <td className="oms-kalem">
                      {o.items.length === 0 ? <span className="oms-yok">—</span> : o.items.map((it, i) => (
                        <span key={i} className="oms-kalem-sat">
                          {it.name} <b className="mono">{fmtQty(it.qty)} {it.uom}</b>
                        </span>
                      ))}
                    </td>
                    <td className="mono oms-teslim">
                      <span className={o.gecikti ? "gec" : undefined}>{fmtDate(o.teslimTarihi)}</span>
                      {o.teslimTahmini ? <span className="oms-yildiz" title="Söz verilen tarih girilmemiş — istenen tarih">*</span> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          )
        )}
      </div>
    </div>
  );
}
